/**
 * Skeleton placeholders (instead of spinners) while a screen's code or content is on its way: the shape of the screen
 * (map path, card shelves, grid, report rows, lesson stage) in soft paper tones with a slow shimmer (none with reduced
 * motion / battery saver). `RouteSkeleton` picks the shape from the URL (lazy-route fallback).
 */
import { useLocation } from 'react-router-dom';
import { useT } from '../lib/i18n';
import './Skeleton.css';

export type SkeletonKind = 'map' | 'shelves' | 'grid' | 'report' | 'lesson' | 'wall' | 'list' | 'generic';

const B = ({ w, h, r = 14, className = '' }: { w?: string | number; h: number | string; r?: number; className?: string }) => (
  <span className={`sk-b ${className}`} style={{ width: w ?? '100%', height: h, borderRadius: r }} />
);

export function Skeleton({ kind = 'generic', className = '', testId = 'screen-loading' }: { kind?: SkeletonKind; className?: string; testId?: string }) {
  const t = useT();
  return (
    <div className={`skeleton sk-${kind} ${className}`} aria-busy="true" aria-label={t('loadingScreen')} role="status" data-testid={testId} data-skeleton={kind}>
      {kind === 'map' && (
        <div className="sk-map">
          <B h={34} w="40%" />
          <div className="sk-path">
            {Array.from({ length: 7 }, (_, i) => (
              <B key={i} h={64} w={64} r={32} className={`sk-node sk-n${i}`} />
            ))}
          </div>
        </div>
      )}
      {kind === 'shelves' &&
        Array.from({ length: 3 }, (_, r) => (
          <div key={r} className="sk-shelf">
            <B h={22} w="28%" />
            <div className="sk-row">
              {Array.from({ length: 6 }, (_, i) => (
                <B key={i} h={128} w={150} />
              ))}
            </div>
          </div>
        ))}
      {(kind === 'grid' || kind === 'wall') && (
        <>
          <B h={30} w="36%" />
          <div className={kind === 'wall' ? 'sk-wall' : 'sk-grid'}>
            {Array.from({ length: kind === 'wall' ? 5 : 12 }, (_, i) => (
              <B key={i} h={kind === 'wall' ? 150 : 104} />
            ))}
          </div>
        </>
      )}
      {(kind === 'report' || kind === 'list') && (
        <>
          <B h={30} w="42%" />
          <div className="sk-cards">
            {Array.from({ length: 3 }, (_, i) => (
              <B key={i} h={88} />
            ))}
          </div>
          {Array.from({ length: kind === 'report' ? 4 : 6 }, (_, i) => (
            <B key={i} h={18} w={`${86 - i * 9}%`} r={8} />
          ))}
        </>
      )}
      {kind === 'lesson' && (
        <div className="sk-lesson">
          <B h="100%" className="sk-paper" r={20} />
          <div className="sk-side">
            <B h={26} w="70%" />
            <B h={16} w="90%" r={8} />
            <B h={16} w="80%" r={8} />
            <B h={52} w="60%" r={26} />
          </div>
        </div>
      )}
      {kind === 'generic' && (
        <>
          <B h={30} w="40%" />
          <B h={160} />
          <B h={18} w="70%" r={8} />
        </>
      )}
    </div>
  );
}

const ROUTE_KIND: [RegExp, SkeletonKind][] = [
  [/^\/(learn)?$/, 'map'],
  [/^\/playground/, 'shelves'],
  [/^\/(collect|journal)/, 'grid'],
  [/^\/exhibition/, 'wall'],
  [/^\/parents/, 'report'],
  [/^\/(lesson|studio\/flow|together)/, 'lesson'],
  [/^\/(settings|account|profiles|skills|challenges|letters|tales)/, 'list'],
];

/** Lazy-route fallback: the skeleton of the screen being opened. */
export function RouteSkeleton() {
  const { pathname } = useLocation();
  const kind = ROUTE_KIND.find(([re]) => re.test(pathname))?.[1] ?? 'generic';
  return <Skeleton kind={kind} />;
}
